import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ROUTES } from "@/app/routes";
import { applyNewSession } from "@/features/identity/apply-new-session";
import { AccountPicker } from "@/features/identity/components/account-picker";
import {
  type AccountCandidate,
  completeSignup,
  pickLoginAccount,
  verifyLoginToken,
  verifySignupToken,
} from "@/features/identity/identity.api";
import {
  markRegistrationCompleted,
  trackSignupStarted,
  trackSignupVerified,
} from "@/shared/analytics/registration-analytics";
import {
  consentRequestIdOf,
  hasPendingConsent,
  isSafeOAuthReturnTo,
} from "@/shared/auth/oauth-return-to";
import { LogoLockup } from "@/shared/components/brand/logo-mark";
import { Button } from "@/shared/components/ui/button";

const route = getRouteApi(ROUTES.auth.verify.id);

/// Tokens we've already started (or finished) verifying in this tab. Magic
/// link tokens are single-use, so re-firing the effect on a re-mount (React
/// strict mode in dev, or any parent re-render that thrashes this route)
/// would consume the token on call 1 and then 404 on call 2, landing the
/// user on /auth/error after the first call already signed them in. We
/// gate the effect on a module-level Set keyed by token so each token is
/// only verified once per page load.
const VERIFIED_TOKENS = new Set<string>();

type VerifyOutcome =
  | { kind: "signed-in" }
  | { kind: "signed-up" }
  | { kind: "pick-account"; token: string; candidates: AccountCandidate[] }
  | { kind: "failed" };

async function completeMagicLinkSignup(signupToken: string): Promise<void> {
  trackSignupStarted("magic_link");
  trackSignupVerified("magic_link");
  await completeSignup({ token: signupToken });
  markRegistrationCompleted("magic_link");
}

/// The shore mints magic-link tokens with intent "login" OR "signup" and
/// emails the same URL shape for both, so try login first (the common case)
/// and fall back to signup.
async function verifyMagicLink(token: string): Promise<VerifyOutcome> {
  try {
    const login = await verifyLoginToken({ token });
    if (login.action === "signed-in") return { kind: "signed-in" };
    if (login.action === "pick-account") {
      return { kind: "pick-account", token: login.token, candidates: login.candidates };
    }
    await completeMagicLinkSignup(login.token);
    return { kind: "signed-up" };
  } catch {
    try {
      const signup = await verifySignupToken({ token });
      await completeMagicLinkSignup(signup.token);
      return { kind: "signed-up" };
    } catch {
      return { kind: "failed" };
    }
  }
}

/**
 * Magic-link click landing, `/auth/verify?token=...&returnTo=...`.
 *
 *   signed in             → `returnTo` (full reload) or "/"
 *   signed up             → "/?firstRun=1"
 *   several accounts      → account picker, then as signed in
 *   failed                → /auth/error
 *
 * A consent `returnTo` is followed only in the browser that started the AI
 * app's Connect (see `rememberPendingConsent`). Anywhere else the link waits:
 * signing in here would end on a consent screen whose approval returns to an
 * app on another device.
 */
export function VerifyRoute() {
  const { token, returnTo } = route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const consentReturn = isSafeOAuthReturnTo(returnTo) ? returnTo : undefined;
  const [startedHere] = useState(
    () => !consentReturn || hasPendingConsent(consentRequestIdOf(consentReturn) ?? ""),
  );
  const [signInHereInstead, setSignInHereInstead] = useState(false);
  const [picking, setPicking] = useState<{ token: string; candidates: AccountCandidate[] }>();
  const waitingForChoice = !startedHere && !signInHereInstead;
  const destination = signInHereInstead ? undefined : consentReturn;

  const pick = useMutation({ mutationFn: pickLoginAccount });

  useEffect(() => {
    async function landSignedIn(firstRun: boolean): Promise<void> {
      if (destination) {
        // reason: full reload into the consent page so it boots with the new
        // session instead of the anonymous /auth/me this tab cached.
        window.location.assign(destination);
        return;
      }
      await applyNewSession(qc);
      await navigate({ to: "/", search: firstRun ? { firstRun: 1 } : {} });
    }

    if (!token) {
      void navigate({ to: "/auth/error", search: { reason: "invalid-or-expired" } });
      return;
    }
    if (waitingForChoice || VERIFIED_TOKENS.has(token)) return;
    VERIFIED_TOKENS.add(token);

    void verifyMagicLink(token).then(async (outcome) => {
      if (outcome.kind === "signed-in") return landSignedIn(false);
      if (outcome.kind === "signed-up") return landSignedIn(true);
      if (outcome.kind === "pick-account") return setPicking(outcome);
      await navigate({ to: "/auth/error", search: { reason: "invalid-or-expired" } });
    });
  }, [token, waitingForChoice, destination, navigate, qc]);

  async function onPickAccount(pickToken: string, userId: string) {
    try {
      await pick.mutateAsync({ token: pickToken, userId });
    } catch {
      await navigate({ to: "/auth/error", search: { reason: "invalid-or-expired" } });
      return;
    }
    if (destination) {
      window.location.assign(destination);
      return;
    }
    await applyNewSession(qc);
    await navigate({ to: "/", search: {} });
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-12">
      <LogoLockup className="w-fit" />
      {picking ? (
        <AccountPicker
          reason="Your email is on more than one Pulsewise account."
          candidates={picking.candidates}
          pending={pick.isPending}
          onPick={(userId) => void onPickAccount(picking.token, userId)}
        />
      ) : waitingForChoice ? (
        <div className="flex flex-col gap-4">
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            Finish signing in on your other browser
          </h1>
          <p className="text-sm text-text-secondary">
            You started connecting an AI app in a different browser. Go back to that browser and
            enter the 6-digit code from this email.
          </p>
          <Button variant="outline" className="w-fit" onClick={() => setSignInHereInstead(true)}>
            Sign in on this browser instead
          </Button>
        </div>
      ) : (
        <p className="text-sm text-text-secondary">Verifying your link…</p>
      )}
    </div>
  );
}
